<template>
  <vl-grid>
    <vl-column width="12" width-xs="12">
      <vl-title tag-name="h1">
        {{ isAdmin ? $t('publications.title') : $t('publications.me.title') }}
      </vl-title>
    </vl-column>
    <vl-column width="12" width-xs="12">
      <LatestPublicationFilter :options="options" @search="doSearch" @clear="clear"></LatestPublicationFilter>
    </vl-column>
  </vl-grid>
  <vl-grid v-vl-spacer:top.medium>
    <vl-column>
      <vl-data-table-extended
        :key="tableKey"
        :data="items"
        :columns="columnMap"
        :fetching="loading"
        :meta="metaData"
        mod-clickable-rows
        mod-zebra
        @column-clicked="columnClicked"
        @pager-clicked="pagerClicked"
      >
        <template v-slot:noresults>
          <template v-if="loading">
            <vl-region>
              <div v-vl-align:center>
                <vl-loader :message="$t('publications.list.loading')" />
              </div>
            </vl-region>
          </template>
          <template v-else-if="isSearchable">
            <div v-if="isAdmin" class="vl-u-spacer--large">
              <vl-title tag-name="h2">{{ $t('publications.list.table.noResults.title') }}</vl-title>
              <p>{{ $t('publications.list.table.noResults.subtitle') }}</p>
            </div>
            <div v-else>
              <vl-region>
                <div v-vl-align:center>
                  <p>
                    {{
                      onlyActive
                        ? $t('publications.list.table.noResults.noActivePublications')
                        : $t('publications.list.table.noResults.noPublications')
                    }}
                  </p>
                  <p v-if="onlyActive">
                    <vl-button mod-link type="button" @click="showAllPublications">
                      {{ $t('publications.list.table.noResults.showAllPublications') }}
                    </vl-button>
                  </p>
                </div>
              </vl-region>
            </div>
          </template>
        </template>
      </vl-data-table-extended>
    </vl-column>
  </vl-grid>
</template>
<script lang="ts" setup>
import { h, onBeforeMount, onUnmounted, ref, watch, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { RowData } from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import PbsIconWrapper from '@/components/icons/PbsIconWrapper.vue';
import LatestPublicationFilter from './LatestPublicationFilter.vue';
import { PublicationListOptions, usePublicationListStore } from '@/modules/publications/store/publication-list.store';
import { ColumnDefExtended } from '@/components/vl-data-table-extended/types';
import { ProductGroup, PublicationSearchResult, PublicationType } from '@/api/frontoffice/clients';
import { dateFilter } from '@/filters';
import { OrganisationSearchResultDto } from '@/api/backoffice/clients';
import { ENUM_PublicationTypes } from '@/modules/core/common';
import { useUserStore } from '@/store/user.store';
import { IAlert, useAlertStore } from '@/modules/core/store/alert.store';

const { t } = useI18n();
const store = usePublicationListStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const tableKey = ref(0);
const isSearchable = ref(false);

const loading = computed((): boolean => {
  return store.loading;
});

const alert = computed((): IAlert => {
  return useAlertStore().alert;
});

const isAdmin = computed(() => {
  return userStore.isSysteembeheerder;
});

const options = computed((): PublicationListOptions => {
  return store.options;
});

const items = computed((): PublicationSearchResult & { routerLink?: string }[] => {
  return store.publications?.results?.map((i) => {
    return {
      ...i,
      routerLink: `/publication/${i.productDefinitionId}`
    };
  });
});

const metaData = computed(() => {
  return store.metaData;
});

const columnMap = computed((): ColumnDefExtended<RowData>[] => {
  const items = [
    {
      accessorKey: 'productLabel',
      header: t('publications.list.table.header.label'),
      enableSorting: true,
      sortDescFirst: false,
      size: 20,
      link: true
    },
    {
      accessorKey: 'publicationType',
      header: t('publications.list.table.header.type'),
      enableSorting: true,
      size: 5,
      link: true,
      cell: (props: any) => {
        const publicationType = props.row.original.publicationType;
        const label =
          publicationType == ENUM_PublicationTypes.Datasetserie.id
            ? t('publications.datasetserie')
            : t('publications.dataset');

        const iconName = publicationType == ENUM_PublicationTypes.Datasetserie.id ? 'file-copy' : 'file';

        return h('span', [
          h(PbsIconWrapper, {
            icon: iconName,
            modLarge: false
          }),
          ' ' + label
        ]);
      }
    },
    {
      accessorKey: 'publicationStatus',
      header: t('publications.list.table.header.status'),
      enableSorting: true,
      size: 2,
      link: true,
      cell: (props: any) => {
        const status = props.row.original.publicationStatus;
        return t(`publications.states.publication.${status?.toLowerCase()}`);
      }
    },
    {
      accessorKey: 'versionDate',
      header: t('publications.list.table.header.versionDate'),
      enableSorting: true,
      sortDescFirst: true,
      size: 2,
      link: true,
      cell: (props: any) => {
        const dateString = props.row.original.versionDate;
        return dateFilter(dateString);
      }
    },
    {
      accessorKey: 'publicationDate',
      header: t('publications.list.table.header.publicationDate'),
      enableSorting: true,
      sortDescFirst: true,
      size: 2,
      link: true,
      cell: (props: any) => {
        const dateString = props.row.original.publicationDate;
        return dateFilter(dateString);
      }
    }
  ];

  if (store.filter.onlyActive) {
    return items.filter((i) => i.accessorKey !== 'publicationStatus');
  }
  return items;
});

onBeforeMount(() => {
  store.init(createOptionsFromRoute());
});

onUnmounted(() => {
  store.clear();
});

const onlyActive = computed((): boolean => {
  return store.filter.onlyActive;
});

watch(
  () => store.filter.onlyActive,
  () => {
    tableKey.value += 1; // Increment the key to force re-render
  },
  { deep: true }
);

const showAllPublications = async () => {
  const newFilter = reactive({
    ...store.filter,
    onlyActive: false
  });
  store.updateFilter(newFilter);
  await search({ resetPager: true });
};

const doSearch = async (
  filter: {
    name?: string;
    organisation?: OrganisationSearchResultDto;
    productGroup?: ProductGroup;
    type?: string;
    status?: string;
    onlyActive?: boolean;
  },
  { resetPager }: { resetPager: boolean } = { resetPager: true }
) => {
  // searching is controlled by the filter component, and we have to wait for the component to be mounted and ready
  isSearchable.value = true;

  store.updateFilter({
    organisationId: filter.organisation?.id || null,
    productGroupLabel: filter.productGroup?.label || null,
    name: filter.name,
    status: filter.status,
    type: filter.type
      ? filter.type === ENUM_PublicationTypes.Datasetserie.name
        ? PublicationType.Datasetserie
        : PublicationType.Dataset
      : null,
    onlyActive: filter.onlyActive
  });
  await search({ resetPager });
};

const clear = async () => {
  store.clear();
  await search();
};

const search = async ({ resetPager }: { resetPager: boolean } = { resetPager: false }) => {
  if (resetPager) {
    store.resetPager();
  }

  await store.getLatestPublications();

  if (!alert.value) {
    updateRouteFromOptions();
  }
};

const pagerClicked = (pageNumber: number) => {
  store.updatePage(pageNumber);
  search();
};

const createOptionsFromRoute = (): PublicationListOptions => {
  // make a deep copy of the default options so that we don't modify the store's defaultOptions
  const options = JSON.parse(JSON.stringify(store.defaultOptions));
  Object.keys(route.query).forEach((k) => {
    let val = route.query[k];
    if (k === 'pageNumber' || k === 'pageSize') {
      options.paging[k] = parseInt(val as string);
    } else if (k === 'by') {
      options.sorting[k] = val as string;
    } else if (k === 'descending') {
      options.sorting[k] = val === 'true';
    } else if (k === 'onlyActive') {      
      options.filter[k] = val === 'true';
    } else if (k === 'type') {
      options.filter[k] = val === 'Datasetserie' ? PublicationType.Datasetserie : PublicationType.Dataset;
    } else {
      options.filter[k] = val as string | number | boolean;
    }
  });
  return options;
};

const updateRouteFromOptions = () => {
  const values = {
    ...store.filter,
    ...store.paging,
    ...store.sorting
  };

  const query = {};
  Object.keys(values).forEach((k) => {
    let val = values[k];
    if (val !== undefined && val !== null && val !== '') {
      if (k === 'type') {
        if (val === ENUM_PublicationTypes.Datasetserie.id) {
          val = 'Datasetserie' as any;
        } else if (val === ENUM_PublicationTypes.Dataset.id) {
          val = 'Dataset' as any;
        }
      }
      query[k] = val;
    } else {
      delete query[k];
    }
  });

  router.push({
    ...route,
    path: route.path,
    query
  });
};

const columnClicked = (evt: { key: string; direction: string }) => {
  store.updateSorting(evt.key, evt.direction);
  search({ resetPager: true });
};
</script>

<style lang="scss" scoped></style>
